import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { Save } from "lucide-react";

import axiosApi from "../server/service";
import { useDesignFormStore, SizeSegment } from "../store/DesignMasterStore"; // Make sure SizeSegment is exported from your store

import BasicInfoTab from "./DesignmasterTabs/BasciInfo";
import FabricSpecsTab from "./DesignmasterTabs/FabricSpecs";
import AstarTab from "./DesignmasterTabs/Astar";
import DupattaTab from "./DesignmasterTabs/Dupatta";
import AdditionalFabricTab from "./DesignmasterTabs/AdditionalFabric";
import ImagesTab from "./DesignmasterTabs/ImageTab";
import FabricUsageTab from "./DesignmasterTabs/Fabricusages";
import OverallCostingTab from "./DesignmasterTabs/overallCosting";
import WarningModal from "./WarningModal";

const tabs = [
  { id: "basic_info", label: "Basic Info" },
  { id: "images", label: "Images" },
  { id: "fabric_specs", label: "Fabric Specs" },
  { id: "astar", label: "Astar" },
  { id: "dupatta", label: "Dupatta" },
  { id: "fabric_usage", label: "Fabric usage" },
  { id: "additional_fabric", label: "Additional Fabric" },
  { id: "overall_design", label: "Overall Design" },
];

const SmoothTabs = ({
  activeTab,
  setActiveTab,
}: {
  activeTab: string;
  setActiveTab: (id: string) => void;
}) => {
  return (
    <div className="relative rounded-sm flex  items-center justify-between overflow-x-auto whitespace-nowrap px-1 py-2 bg-gray-100 scrollbar-hide">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`${
            activeTab === tab.id ? "" : "hover:text-gray-600"
          } relative rounded-sm  px-9 py-1 text-sm font-medium text-black transition focus-visible:outline-2`}
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          {activeTab === tab.id && (
            <motion.span
              layoutId="bubble"
              className="absolute inset-0 z-0 bg-white shadow-sm"
              style={{ borderRadius: 6 }}
              transition={{ type: "spring", bounce: 0.2, duration: 1 }}
            />
          )}
          <span
            className={`relative z-10 ${
              activeTab === tab.id ? "text-black" : "text-gray-700"
            }`}
          >
            {tab.label}
          </span>
        </button>
      ))}
    </div>
  );
};

const CreateDesignPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("basic_info");
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { designId } = useParams();
  const navigate = useNavigate();

  const hasShownToast = useRef(false);

  const {
    getFormData,
    validateForm,
    setFormData,
    resetAll,
    setSizeSegments, // Make sure this is in your Zustand store
  } = useDesignFormStore();

  useEffect(() => {
    return () => {
      resetAll();
    };
  }, [resetAll]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (designId) {
        try {
          const [designResponse, allSegmentsResponse] = await Promise.all([
            axiosApi.get(`/api/designs/${designId}`),
            axiosApi.get("/api/sizeSegment/get-SizeSegments-For-Design"),
          ]);

          const designData = designResponse.data.data;
          const allSegmentsApi = allSegmentsResponse.data;

          const allSegments: SizeSegment[] = allSegmentsApi.map((s: any) => ({
            id: s._id,
            name: s.segmentName,
          }));

          const usedSegmentIds = new Set<string>();
          if (designData.fabricUsage) {
            Object.values(designData.fabricUsage).forEach((category: any) => {
              if (category && category.rows) {
                category.rows.forEach((row: any) => {
                  if (row.values) {
                    Object.keys(row.values).forEach((segmentId) =>
                      usedSegmentIds.add(segmentId)
                    );
                  }
                });
              }
            });
          }

          // 3. Create the final list of segments to show in the table headers
          const segmentsToShow = allSegments.filter((segment) => {
            const fullSegment = allSegmentsApi.find(
              (s: any) => s._id === segment.id
            );
            // Show a segment if it's currently ACTIVE...
            return (
              fullSegment.status === "Active" ||
              // ...OR if it was USED in this design (even if it's now inactive)
              usedSegmentIds.has(segment.id)
            );
          });

          // 4. Set the form data and the correct list of segments in the store
          setFormData(designData);
          setSizeSegments(segmentsToShow);

          if (!hasShownToast.current) {
            toast.success("Design loaded for editing!");
            hasShownToast.current = true;
          }
        } catch (error) {
          console.error("Failed to load design for editing:", error);
          toast.error("Failed to load design data.");
        }
      } else {
        // --- CREATE MODE LOGIC ---
        try {
          resetAll(); // Start with a fresh form
          // Fetch only the ACTIVE segments using the original endpoint
          const response = await axiosApi.get(
            "/api/sizeSegment/get-all-segments"
          );
          const activeSegments = response.data.map((s: any) => ({
            id: s._id,
            name: s.segmentName,
          }));
          setSizeSegments(activeSegments);
        } catch (error) {
          console.error("Failed to fetch active size segments:", error);
          toast.error("Failed to load size segments.");
        }
      }
    };

    loadInitialData();
  }, [designId, setFormData]); // Dependencies for the effect

  const handleConfirmSave = async () => {
    setIsWarningOpen(false);
    setIsSubmitting(true);
    const currentData = getFormData();

    const formData = new FormData();

    // Helper to safely stringify and append
    const appendJSON = (key: string, data: any) => {
      if (data) {
        formData.append(key, JSON.stringify(data));
      }
    };

    // Ensure designWork is included in fabricUsage
    const fabricUsageToSend = {
      ...currentData.fabricUsage,
      designWork: currentData.fabricUsage.designWork || {
        rows: [],
        selection: null,
        options: [],
      },
    };

    // Debug: Verify designWork is included
    console.log("FabricUsage being sent:", {
      hasDesignWork: !!fabricUsageToSend.designWork,
      designWorkRows: fabricUsageToSend.designWork?.rows,
      allKeys: Object.keys(fabricUsageToSend),
    });

    appendJSON("basicInfo", currentData.basicInfo);
    appendJSON("fabricSpecs", currentData.fabricSpecs);
    appendJSON("astar", currentData.astar);
    appendJSON("dupatta", currentData.dupatta);
    appendJSON("additionalFabric", currentData.additionalFabric);
    appendJSON("fabricUsage", fabricUsageToSend);
    appendJSON("overallCosting", currentData.overallCosting);
    appendJSON("notes", currentData.notes);

    // Append files if they are File objects
    if (currentData.images.primaryImage instanceof File)
      formData.append("primaryImage", currentData.images.primaryImage);
    if (currentData.images.additionalImage1 instanceof File)
      formData.append("additionalImage1", currentData.images.additionalImage1);
    if (currentData.images.additionalImage2 instanceof File)
      formData.append("additionalImage2", currentData.images.additionalImage2);
    if (currentData.images.additionalImage3 instanceof File)
      formData.append("additionalImage3", currentData.images.additionalImage3);
    if (currentData.images.additionalImage4 instanceof File)
      formData.append("additionalImage4", currentData.images.additionalImage4);

    // If editing, send existing image URLs back to the server
    if (designId) {
      const existingImages = {
        primaryImage:
          typeof currentData.images.primaryImage === "string"
            ? currentData.images.primaryImage
            : null,
        additionalImage1:
          typeof currentData.images.additionalImage1 === "string"
            ? currentData.images.additionalImage1
            : null,
        additionalImage2:
          typeof currentData.images.additionalImage2 === "string"
            ? currentData.images.additionalImage2
            : null,
        additionalImage3:
          typeof currentData.images.additionalImage3 === "string"
            ? currentData.images.additionalImage3
            : null,
        additionalImage4:
          typeof currentData.images.additionalImage4 === "string"
            ? currentData.images.additionalImage4
            : null,
      };
      formData.append("existingImages", JSON.stringify(existingImages));
    }

    try {
      const response = designId
        ? await axiosApi.put(
            `/api/designs/update-designs/${designId}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          )
        : await axiosApi.post("/api/designs/save-design", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

      toast.success(response.data.message);
      navigate("/designmaster");
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.message || "An unknown error occurred.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = () => {
    const { valid, missingFields } = validateForm();
    if (!valid) {
      setWarnings(missingFields);
      setIsWarningOpen(true);
      return;
    }
    handleConfirmSave();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "basic_info":
        return <BasicInfoTab />;
      case "images":
        return <ImagesTab />;
      case "fabric_specs":
        return <FabricSpecsTab />;
      case "astar":
        return <AstarTab />;
      case "dupatta":
        return <DupattaTab />;
      case "fabric_usage":
        return <FabricUsageTab />;
      case "additional_fabric":
        return <AdditionalFabricTab />;
      case "overall_design":
        return <OverallCostingTab />;
      default:
        return (
          <div className="text-center py-10 text-gray-500">
            Content for {activeTab.replace("_", " ")} goes here.
          </div>
        );
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold text-black text-xl sm:text-2xl md:text-3xl">
            {designId ? "Edit Design" : "Create New Design"}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base md:text-[17px] mt-1">
            Configure design specifications and fabric usage
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/DesignMaster"
            className="border border-white bg-white rounded-lg px-6 py-2.5"
          >
            Cancel
          </Link>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors disabled:bg-gray-400"
          >
            <Save className="w-4 h-4 text-white" />
            <span>{isSubmitting ? "Saving..." : "Save Design"}</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-100 rounded-sm p-1 mt-6">
        <SmoothTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      {/* Tab Content */}
      <div className="mt-6 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        {renderTabContent()}
      </div>

      <WarningModal
        isOpen={isWarningOpen}
        warnings={warnings}
        onConfirm={handleConfirmSave}
        onCancel={() => setIsWarningOpen(false)}
      />
    </div>
  );
};

export default CreateDesignPage;
